import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface StatementImport {
  id: string;
  bank_account_id: string;
  file_name: string;
  file_format: string;
  date_from: string | null;
  date_to: string | null;
  transaction_count: number | null;
  status: string;
  error_message: string | null;
  created_at: string;
  created_by: string | null;
}

export const useStatementImports = (bankAccountId?: string) => {
  return useQuery({
    queryKey: ['statement-imports', bankAccountId],
    queryFn: async () => {
      let query = supabase
        .from('bank_statement_imports')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (bankAccountId) {
        query = query.eq('bank_account_id', bankAccountId);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data as StatementImport[];
    },
  });
};

export const useCreateStatementImport = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (importData: Omit<StatementImport, 'id' | 'created_at' | 'created_by'>) => {
      const { data: userData } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('bank_statement_imports')
        .insert({
          ...importData,
          created_by: userData?.user?.id,
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['statement-imports'] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to record import: ${error.message}`);
    },
  });
};

export const useDeleteStatementImport = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      // First get the import details
      const { data: importData, error: fetchError } = await supabase
        .from('bank_statement_imports')
        .select('id')
        .eq('id', id)
        .single();
      
      if (fetchError) throw fetchError;
      
      // Delete associated transactions
      const { error: txError } = await supabase
        .from('bank_transactions')
        .delete()
        .eq('import_batch_id', id);
      
      if (txError) throw txError;
      
      // Delete the import record
      const { error } = await supabase
        .from('bank_statement_imports')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['statement-imports'] });
      queryClient.invalidateQueries({ queryKey: ['bank-transactions'] });
      toast.success('Import and transactions deleted');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete import: ${error.message}`);
    },
  });
};
